// Modules
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import VelocityTransitionGroup from 'velocity-react/velocity-transition-group';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { Card } from 'components/content-containers/cards';
import { SHORT_HUMAN_DATE_FORMAT } from 'constants';
import { SVGButton } from 'components/widgets/buttons';
import { alerts } from 'modules/alerts/actions';
import { slideClosed, slideOpen } from 'utils/transition-groups';
import CompanyNickname from 'components/models/company/company-nickname';
import DriveFiles from 'components/models/files/drive-files';
import EditTicketForm from 'components/models/ticket/forms/edit-ticket-form';
import FlipMove from 'components/widgets/animation/flip-move';
import SVG from 'components/widgets/icons/svg';
import TextWithNewlinesLinks from 'components/widgets/text/text-with-newlines-links';
import Ticket from 'services/data/ticket';
import TicketLink from 'components/models/ticket/ticket-link';
import TimeReportForm from 'components/models/time-report/forms/time-report-form';
import Tooltip from 'components/widgets/overlays/tooltip';
import UserBadge from 'components/models/user/user-badge';
import * as timerActions from 'modules/timers/actions';

import AddTask from './add-task';
import ClosingLastTask from './closing-last-task';
import TaskComponent from './task';
import TicketOptions from './ticket-options';


class TicketComponent extends React.Component {
  static propTypes = {
    ticket: PropTypes.object.isRequired,
    updateTicket: PropTypes.func.isRequired,
    updateTask: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    teams: PropTypes.array.isRequired,
    query: PropTypes.object
  };

  constructor() {
    super();
    this.state = {};

    this.editTicket = this.editTicket.bind(this);
    this.addTask = this.addTask.bind(this);
    this.cancel = this.cancel.bind(this);
    this.saveNewTask = this.saveNewTask.bind(this);
    this.addTimeReport = this.addTimeReport.bind(this);
    this.afterSaveTimeReport = this.afterSaveTimeReport.bind(this);
    this.addFolder = this.addFolder.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const nextTicket = _.get(nextProps, 'ticket');
    const currentTicket = _.get(this.props, 'ticket');

    // If the state has changed re-render right away
    if (this.state !== nextState) return true;

    // Only re-render if the task has been updated
    if (nextTicket && currentTicket) return !moment(nextTicket.updatedAt).isSameOrBefore(currentTicket.updatedAt);

    return true;
  }

  addTask() {
    this.props.updateTicket({ addingTask: true, addingTimeReport: false, editingTicket: false });
  }

  addTimeReport() {
    this.props.updateTicket({ addingTask: false, addingTimeReport: true, editingTicket: false });
  }

  editTicket() {
    this.props.updateTicket({ addingTask: false, addingTimeReport: false, editingTicket: true });
  }

  cancel() {
    this.props.updateTicket({ addingTask: false, addingTimeReport: false, editingTicket: false });
  }

  afterSaveTimeReport() {
    this.cancel(); // Close the add time report form
    
    alerts.success({
      title: 'Time report added!',
      subtitle: <TicketLink ticket={this.props.ticket} linkSuffix="/time">View in ticket</TicketLink>
    });
  }

  saveNewTask(description, properties) {
    const task = { description, ...properties };

    return Ticket.saveNewTask(this.props.ticket, task, this.props.updateTask);
  }

  addFolder() {
    const { ticket } = this.props;

    Ticket.update({ id: ticket.id, folder: { filename: ticket.description } })
      .then(savedTicket => {
        this.props.updateTicket({ folder: savedTicket.folder });
      })
      .catch(alerts.catchError);
  }

  render() {
    const { ticket, currentUser, updateTicket, updateTask, teams, query } = this.props;
    const { addingTask, addingTimeReport, editingTicket } = ticket;

    const future = _.get(query, 'where.dueDate.max') && moment(ticket.tasks[0].dueDate).isAfter(query.where.dueDate.max);
    const urgency = Ticket.urgency(ticket);
    const billable = _.get(ticket, 'agreement.id');
    const hideAgreement = _.includes(['Business Development', 'Internal'], ticket.type);

    const initialFormValues = _.assign({
      agreement: hideAgreement ? null : _.get(ticket, 'agreement.id', ticket.agreement),
      company: _.get(ticket, 'company.id', ticket.company)
    }, _.pick(ticket, ['description', 'type', 'id', 'resolution', 'status', 'adminOnly', 'timeDefaultBillable', 'expectedCompletion', 'workOrder']));

    const defaultRate = _.find(_.get(ticket, 'agreement.rates', []), { role: currentUser.billingRole });

    return (
      <Card className={classNames('ticket-card', _.kebabCase(ticket.type), { 'future-ticket': future })}>
        { ticket.showClosingLastTaskModal &&
          <ClosingLastTask
            ticket={ticket}
            updateTask={updateTask}
            updateTicket={updateTicket}
            saveNewTask={this.saveNewTask}
            currentUser={currentUser}
          />
        }
        <div className={classNames('ticket-header', `urgency-${urgency}`)}>
          <div className="ticket-links">
            <TicketLink ticket={ticket} className="ticket-info">
              <h4>{ticket.description}</h4>
              
            </TicketLink>
            <CompanyNickname company={ticket.company} className="deemphasize" />
          </div>
          { billable && <SVGButton onClick={() => timerActions.add(ticket)} title="Start Timer" src="svg/play.svg" className="bg-white text-midnight-blue rounded-circle p-0 mr-2 ml-1" /> }
          { !_.isEmpty(ticket.folder) && <DriveFiles folder={ticket.folder} /> }
          <TicketOptions
            editTicket={this.editTicket}
            addTask={this.addTask}
            addTimeReport={this.addTimeReport}
            addFolder={this.addFolder}
            ticket={ticket}
            currentUser={currentUser}
            teams={teams}
          >
            <SVG className="more-icon" src="svg/menu.svg" />
          </TicketOptions>
        </div>
        { ticket.adminOnly &&
          <Tooltip title="Only Praece Administrators can access this ticket" elementType="div" className="ticket-security-header admin-only">
            <SVG src="svg/shield.svg" className="admin-only" />
            Praece Administrators Only
          </Tooltip>
        }
        <VelocityTransitionGroup component="div" leave={slideClosed} enter={slideOpen} className="form-area">
          { (ticket.resolution && ticket.status === 'Closed') &&
            <div className="ticket-resolution">
              <div className="ticket-resolution-header">
                <span className="deemphasize">
                  Resolution:
                </span>
                <span className="deemphasize">
                  {moment(ticket.closedAt).format(SHORT_HUMAN_DATE_FORMAT)}
                  <UserBadge user={ticket.closedBy} team={_.find(teams, { id: ticket.closedBy.team })} />
                </span>
              </div>
              <TextWithNewlinesLinks text={ticket.resolution} />
            </div>
          }
          { editingTicket &&
            <div className="embedded-ticket-form">
              <EditTicketForm
                initialValues={initialFormValues}
                close={this.cancel}
                ticket={ticket}
                onSubmit={values => updateTicket(_.defaults({ locked: false, editingTicket: false, id: ticket.id }, values))}
                closeOnSubmit
              />
            </div>
          }
          { addingTask &&
            <AddTask
              saveTask={this.saveNewTask}
              ticket={ticket}
              cancelAddTask={this.cancel}
              updateTicket={updateTicket}
              autoFocus
            />
          }
          { addingTimeReport &&
            <div className="embedded-ticket-form">
              <TimeReportForm
                cancel={this.cancel}
                afterSubmit={this.afterSaveTimeReport}
                initialValues={{
                  date: moment(),
                  rate: ticket.timeDefaultBillable ? defaultRate.id : 0,
                  createdBy: currentUser.id,
                  agreement: ticket.agreement.id,
                  ticket: ticket.id,
                  afterHoursPremium: _.get(ticket, 'agreement.afterHoursPremium', 0)
                }}
                ticket={ticket}
                agreement={ticket.agreement}
                form={`time-report-form-${ticket.id}`}
              />
            </div>
          }
        </VelocityTransitionGroup>
        <ul className="tasks unstyled-list">
          <FlipMove>
            {_.map(ticket.tasks, (task) =>
              <TaskComponent
                key={task.uuid}
                task={task}
                updateTask={(updates, ticketUpdates) => updateTask({ ..._.pick(task, ['uuid', 'id', 'ticket']), ...updates }, ticketUpdates)}
                updateTicket={updateTicket}
                ticket={ticket}
              />
            )}
          </FlipMove>
        </ul>
      </Card>
    );
  }
}

export default connect((state) => ({
  currentUser: state.auth.currentUser,
  teams: state.pageData.teams,
  query: state.location.query
}))(TicketComponent);



// WEBPACK FOOTER //
// ./app/components/models/ticket/ticket.js